import React, { useState } from "react";
import "./GeneratedOptionsSelector.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Button from "./Buttons";
import { Flex } from "./Flex";
import Icon, { PictogramIcon } from "./Icon";
import Label from "./Label";
import Paragraph from "./Paragraph";

type Props = {
    title?: string | undefined;
    isLoading?: boolean;
    options: string[];
    collapsed?: boolean;
    collapseOnSelection?: boolean;
    displayOptionsPerPage?: number;
    onSelectOption: (option: string) => void;
    onGenerateMoreOptions?: () => Promise<void>;
};

function GeneratedOptionsSelector(props: Props) {
    const {
        title,
        isLoading,
        options,
        collapsed,
        collapseOnSelection,
        displayOptionsPerPage,
        onSelectOption,
        onGenerateMoreOptions,
    } = props;
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(collapsed ?? false);

    const optionsPerPage = Math.max(1, displayOptionsPerPage ?? 1);
    const [currentPage, setCurrentPage] = useState(1);
    const hasNextPage = currentPage < Math.ceil(options.length / optionsPerPage);
    const hasPreviousPage = currentPage > 1;
    const totalPages = Math.ceil(options.length / optionsPerPage);
    const optionsToDisplay = options.slice(
        (currentPage - 1) * optionsPerPage,
        currentPage * optionsPerPage,
    );

    const nextPage = () => {
        if (hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const previousPage = () => {
        if (hasPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="generated-options-selector">
            <Flex.Column gap="large">
                <Flex.Row
                    className="generated-options-selector__header"
                    justifyContent="space-between"
                    onClick={() => {
                        setIsCollapsed(!isCollapsed);
                    }}
                >
                    {title ? <Label.Medium.N50>{title}</Label.Medium.N50> : ""}
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isCollapsed ? (
                        <Icon.UnfoldMore.Medium />
                    ) : (
                        <Icon.UnfoldLess.Medium />
                    )}
                </Flex.Row>

                {!isCollapsed && (
                    <>
                        {optionsToDisplay.map((option) => (
                            <Button.Ghost
                                key={option}
                                onClick={() => {
                                    onSelectOption(option);
                                    if (collapseOnSelection) setIsCollapsed(true);
                                }}
                            >
                                <div className="generated-options-selector__option">
                                    <Flex.Row gap="large">
                                        <Flex.Column>
                                            <Paragraph.Medium>
                                                {option}
                                            </Paragraph.Medium>
                                        </Flex.Column>
                                        <PictogramIcon.Check.Medium.N90 />
                                    </Flex.Row>
                                </div>
                            </Button.Ghost>
                        ))}

                        {!isLoading && (
                            <Flex.Row justifyContent="space-between">
                                {totalPages > 1 && (
                                    <Flex.Row>
                                        <Button.Ghost
                                            disabled={!hasPreviousPage}
                                            onClick={previousPage}
                                        >
                                            <Icon.ChevronLeft.Medium />
                                        </Button.Ghost>
                                        <Label.Medium.N20>
                                            {Math.min(currentPage, totalPages)}
                                        </Label.Medium.N20>
                                        <Label.Medium.N20>/</Label.Medium.N20>
                                        <Label.Medium.N20>
                                            {totalPages}
                                        </Label.Medium.N20>
                                        <Button.Ghost
                                            disabled={!hasNextPage}
                                            onClick={nextPage}
                                        >
                                            <Icon.ChevronRight.Medium />
                                        </Button.Ghost>
                                    </Flex.Row>
                                )}

                                {onGenerateMoreOptions && (
                                    <Button.Ghost
                                        nowrap
                                        showLoadingSpinner={isLoadingMore}
                                        disabled={isLoading || isLoadingMore}
                                        onClick={async () => {
                                            setIsLoadingMore(true);
                                            await onGenerateMoreOptions();
                                            setIsLoadingMore(false);
                                        }}
                                    >
                                        <Icon.AutoRenew.Medium />
                                        <Label.Medium.SemiBold>
                                            More options
                                        </Label.Medium.SemiBold>
                                    </Button.Ghost>
                                )}
                            </Flex.Row>
                        )}
                    </>
                )}
            </Flex.Column>
        </div>
    );
}

export default GeneratedOptionsSelector;
